import React, { useState, useEffect, useCallback } from "react";
import { API_URLS } from "../../App";
import { CmsPage } from "../../lib/cms/types";
import { extractApiError } from "../../helpers/apiErrors";
import api from "../../hooks/api";
import SectionsBuilder from "./SectionsBuilder";
import { SectionDraft } from "./SectionCard";
import { generateId } from "../../lib/cms/generateId";

interface CmsPageEditorProps {
  page?: CmsPage;
  onSave: (page: CmsPage) => void;
  setError: (error: string | null) => void;
}

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const CmsPageEditor: React.FC<CmsPageEditorProps> = ({
  page,
  onSave,
  setError,
}) => {
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [sections, setSections] = useState<SectionDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [revalidated, setRevalidated] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = useCallback(() => {
    if (!isDirty) setIsDirty(true);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  useEffect(() => {
    setIsDirty(false);
    if (page) {
      setSlug(page.slug);
      setTitle(page.title || "");
      setSeoTitle(page.seo_title || "");
      setSeoDescription(page.seo_description || "");
      setSections(
        page.sections.map((s) => ({
          _id: generateId(),
          ...s,
        })),
      );
    } else {
      setSlug("");
      setTitle("");
      setSeoTitle("");
      setSeoDescription("");
      setSections([]);
    }
  }, [page?.slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedSlug = slug.trim();
    if (!trimmedSlug) {
      setError("O slug é obrigatório.");
      return;
    }
    if (!SLUG_REGEX.test(trimmedSlug)) {
      setError(
        "O slug só pode conter letras minúsculas, números e hífens (ex: minha-pagina).",
      );
      return;
    }

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("O título é obrigatório.");
      return;
    }

    if (sections.length === 0) {
      setError("Adicione pelo menos uma secção à página.");
      return;
    }

    for (const s of sections) {
      if (!s.type) {
        setError("Todas as secções precisam de um tipo definido.");
        return;
      }
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        title: trimmedTitle,
        sections: sections.map(({ _id, ...rest }) => rest),
      };
      const trimmedSeoTitle = seoTitle.trim();
      const trimmedSeoDesc = seoDescription.trim();
      if (trimmedSeoTitle) payload.seo_title = trimmedSeoTitle;
      if (trimmedSeoDesc) payload.seo_description = trimmedSeoDesc;

      const res = await api.put(
        `${API_URLS.CMS_ADMIN}/${trimmedSlug}`,
        payload,
      );
      setIsDirty(false);
      onSave(res.data);
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao guardar página."));
    } finally {
      setSaving(false);
    }
  };

  const handleRevalidate = async () => {
    if (!page) return;
    setRevalidating(true);
    setRevalidated(false);
    setError(null);
    try {
      const res = await fetch("/api/internal/revalidate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tags: [`page-${page.slug}`] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRevalidated(true);
      setTimeout(() => setRevalidated(false), 3000);
    } catch {
      setError("Erro ao revalidar cache. Verifique se o site está acessível.");
    } finally {
      setRevalidating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 max-w-3xl">
      {/* Slug */}
      <div>
        <label
          htmlFor="cms-slug"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Slug *
        </label>
        <input
          id="cms-slug"
          type="text"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            markDirty();
          }}
          disabled={!!page}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          placeholder="nome-da-pagina"
        />
        <p className="text-xs text-gray-500 mt-1">
          Identificador único da página (ex: termos, privacidade). Apenas letras
          minúsculas, números e hífens. Não pode ser alterado após criação.
        </p>
      </div>

      {/* Title */}
      <div>
        <label
          htmlFor="cms-title"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Título *
        </label>
        <input
          id="cms-title"
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            markDirty();
          }}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Título da página"
        />
      </div>

      {/* SEO Title */}
      <div>
        <label
          htmlFor="cms-seo-title"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          SEO Title
        </label>
        <input
          id="cms-seo-title"
          type="text"
          value={seoTitle}
          onChange={(e) => {
            setSeoTitle(e.target.value);
            markDirty();
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Título SEO (opcional)"
        />
      </div>

      {/* SEO Description */}
      <div>
        <label
          htmlFor="cms-seo-desc"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          SEO Description
        </label>
        <textarea
          id="cms-seo-desc"
          value={seoDescription}
          onChange={(e) => {
            setSeoDescription(e.target.value);
            markDirty();
          }}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Descrição SEO (opcional)"
        />
      </div>

      {/* Sections Builder */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Secções
        </label>
        <SectionsBuilder
          sections={sections}
          onChange={(v) => {
            setSections(v);
            markDirty();
          }}
        />
        <p className="text-xs text-gray-500 mt-1">
          As secções são apresentadas por ordem. Use as setas para reordenar.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
        >
          {saving ? "A guardar..." : page ? "Atualizar Página" : "Criar Página"}
        </button>
        {page && (
          <button
            type="button"
            onClick={handleRevalidate}
            disabled={revalidating}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            {revalidating
              ? "A revalidar..."
              : revalidated
                ? "Cache limpo ✓"
                : "Revalidar Cache"}
          </button>
        )}
      </div>
    </form>
  );
};

export default CmsPageEditor;
